import React, { useState, useEffect } from 'react';
import { resignationAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  LogOut, 
  Send, 
  Calendar, 
  Info, 
  Clock, 
  CheckCircle2, 
  XCircle,
  HelpCircle
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const MyResignation = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [reason, setReason] = useState('');
  const [lastWorkingDay, setLastWorkingDay] = useState('');

  const fetchHistory = async () => {
    try {
      const response = await resignationAPI.getMy();
      setHistory(response.data);
    } catch (error) {
      toast.error('Failed to load resignation history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason || !lastWorkingDay) {
      toast.error('Please provide reason and last working day');
      return;
    }

    setSubmitting(true);
    try {
      await resignationAPI.apply({
        reason,
        last_working_day: lastWorkingDay
      });
      toast.success('Resignation request submitted successfully');
      setReason('');
      setLastWorkingDay('');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-700 border-none">Approved</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-700 border-none">Rejected</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700 border-none">Pending</Badge>;
    }
  };

  const hasPendingRequest = history.some(r => r.status === 'PENDING');

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
          <LogOut className="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Exit & Resignation</h1>
          <p className="text-muted-foreground">Apply for resignation or track your exit process.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Application Form */}
        <Card className="lg:col-span-1 shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Send className="h-4 w-4" /> Apply for Resignation
            </CardTitle>
            <CardDescription>Initiate your exit process with the company.</CardDescription>
          </CardHeader>
          <CardContent>
            {hasPendingRequest ? (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-100 text-amber-800 text-sm flex gap-3">
                <Clock className="h-5 w-5 shrink-0" />
                <p>You already have a pending resignation request. Please wait for HR/Manager to process it.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="lastWorkingDay">Proposed Last Working Day</Label>
                  <div className="relative">
                    <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input 
                      id="lastWorkingDay" 
                      type="date"
                      className="pl-9"
                      value={lastWorkingDay}
                      onChange={(e) => setLastWorkingDay(e.target.value)}
                      required
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground italic flex items-center gap-1">
                    <Info className="h-3 w-3" /> Please consider your notice period.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Leaving</Label>
                  <Textarea 
                    id="reason" 
                    placeholder="Briefly explain your reason for separation..."
                    className="min-h-[120px]"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Resignation'}
                </Button>
              </form>
            )}
          </CardContent>
          <CardFooter className="bg-slate-50 border-t p-4 text-[11px] text-slate-500 rounded-b-lg">
            <HelpCircle className="h-3 w-3 mr-1" /> All exits are subject to HR policy and notice period compliance. 
          </CardFooter>
        </Card>

        {/* History Table */}
        <Card className="lg:col-span-2 shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">My Request History</CardTitle>
            <CardDescription>Track status of your previous applications.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead>Applied Date</TableHead>
                  <TableHead>LWD</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Admin Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                     <TableCell colSpan={4} className="h-24 text-center text-sm">Loading history...</TableCell>
                  </TableRow>
                ) : history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center text-muted-foreground italic gap-1">
                        <LogOut className="h-8 w-8 opacity-20" />
                        <span>No resignation history found.</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="text-sm font-medium">
                        {format(new Date(request.applied_at), 'dd MMM, yyyy')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(request.last_working_day), 'dd MMM, yyyy')}
                      </TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground italic max-w-[200px] truncate">
                        {request.remarks || '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyResignation;
